import React, { useEffect, useRef, useState } from 'react';
import i18n from '../../i18n';
import * as precheckService from '../../services/precheckService';
import { useLocation } from 'react-router-dom';
import { Field, Input, Label } from '@headlessui/react';
import { useFormik } from 'formik';
import { dynamicPrecheckFormSchema } from './validationSchemas';
import { initialValues } from './precheckFormConfig';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import LanguageSwitcher from '../shared/LanguageSwitcher';
import BrandLogo from '../shared/BrandLogo';
import { useScrollToError } from '../../hooks/useScrollToError';
import GeneralInformation from './GeneralInformation';
import CreditCardProcessing from './services/CreditCardProcessing';
import AffiliatePayoutProgram from './services/AffiliatePayoutProgram';
import ClearingHouse from './services/ClearingHouse';
import WhitelabelGateway from './services/WhitelabelGateway';
import CorporateBanking from './services/CorporateBanking';
import AlternativeBilling from './services/AlternativeBilling';
import { FormSelect, ErrorSummary, SuccessMessage, ErrorMessage, SubmitButton, SectionHeader } from '../form';
import { parseApiError } from '../../api/apiError';

const captchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const PrecheckForm: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [validationSchema, setValidationSchema] = useState(dynamicPrecheckFormSchema(initialValues.srv));
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [errorDetails, setErrorDetails] = useState<string[]>([]);
  const [captchaReady, setCaptchaReady] = useState(false);
  const submittingRef = useRef(false);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const brand = queryParams.get('brand');
  const isPublicPath = location.pathname.includes('/p/');

  useEffect(() => {
    if (!isPublicPath) return;

    const markReady = () => {
      window.grecaptcha.enterprise.ready(() => setCaptchaReady(true));
    };

    if (window.grecaptcha?.enterprise) {
      markReady();
      return;
    }

    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-recaptcha-enterprise="true"]',
    );
    if (existing) {
      existing.addEventListener('load', markReady, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${captchaSiteKey}`;
    script.dataset.recaptchaEnterprise = 'true';
    script.onload = markReady;
    document.body.appendChild(script);
  }, [isPublicPath]);

  const getCaptchaToken = async (): Promise<string> => {
    if (!captchaReady) {
      throw new Error('reCAPTCHA not ready');
    }
    return window.grecaptcha.enterprise.execute(captchaSiteKey, { action: 'submit' });
  };

  const formik = useFormik({
    initialValues,
    // validationSchema: undefined,
    // validationSchema: dynamicPrecheckFormSchema(formik.values.srv),
    validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values, { resetForm, setFieldError }) => {
      if (submittingRef.current) return;
      submittingRef.current = true;

      setErrorMessage('');
      setErrorDetails([]);

      if (isPublicPath && !captchaReady) {
        alert('reCAPTCHA is not ready yet. Please wait a moment and try again.');
        submittingRef.current = false;
        return;
      }

      const payload = {
        reg: values.reg, // region
        srv: values.srv, // services
        sourceDomain: theme.domain,

        genInfo: {
          genComp: values.genComp,
          genAddr: values.genAddr,
          genDirName: values.genDirName,
          genDirEmail: values.genDirEmail,
          genDirAddr: values.genDirAddr,
          genTaxID: values.genTaxID,
          genDesc: values.genDesc,
          genPrice: values.genPrice,
          genUrl: values.genUrl,
          genFullName: values.genFullName,
          genEmail: values.genEmail,
          genSkype: values.genSkype,
          genPhone: values.genPhone,
          genRefSource: values.genRefSource,
        },
        ccpInfo: {
          ccpFulfill: values.ccpFulfill,
          ccpCallCtr: values.ccpCallCtr,
          ccpLic: values.ccpLic,
          ccpLicJur: values.ccpLicJur,
          ccpBankAcq: values.ccpBankAcq,
          ccpCurrMCC: values.ccpCurrMCC,
          ccpAvgTxVal: values.ccpAvgTxVal,
          ccpCurrMonthVol: values.ccpCurrMonthVol,
          ccpExpMonthVol: values.ccpExpMonthVol,
          ccpDescLine1: values.ccpDescLine1,
          ccpDescLine2: values.ccpDescLine2,
          ccpPrefGateway: values.ccpPrefGateway,
          ccpProcCurr: values.ccpProcCurr,
          ccpSettleCurr: values.ccpSettleCurr,
          ccpTargetMarkets: values.ccpTargetMarkets,
        },

        apInfo: {
          apRegNum: values.apRegNum,
          apUsage: values.apUsage,
          apProjVolume: values.apProjVolume,
          apProjCount: values.apProjCount,
          apAvgTxValue: values.apAvgTxValue,
          apCardCount: values.apCardCount,
          apCurrencies: values.apCurrencies,
          apProduct: values.apProduct,
        },

        wlInfo: {
          wlExpMonthVol: values.wlExpMonthVol,
          wlExpTxCount: values.wlExpTxCount,
          wlAvgTxVal: values.wlAvgTxVal,
          wlLicense: values.wlLicense,
          wlLicJur: values.wlLicJur,
          wlGatewayFeatures: values.wlGatewayFeatures,
          wlOtherGateway: values.wlOtherGateway,
          wlTargetMarkets: values.wlTargetMarkets,
          wlBankAcquirers: values.wlBankAcquirers,
          wlAltBilling: values.wlAltBilling,
          wlOtherRemarks: values.wlOtherRemarks,
        },

        chInfo: {
          chPayee: values.chPayee,
          chClient: values.chClient,
          chCustEmail: values.chCustEmail,
          chMktDesc: values.chMktDesc,
          chCompHandling: values.chCompHandling,
          chRefundPol: values.chRefundPol,
          chAchProvider: values.chAchProvider,
          chAchName: values.chAchName,
          chAchPhone: values.chAchPhone,
          chAchEmail: values.chAchEmail,
          chHighTxAmt: values.chHighTxAmt,
          chAvgTxAmt: values.chAvgTxAmt,
          chMaxTxPerDay: values.chMaxTxPerDay,
          chReqMonthLimit: values.chReqMonthLimit,
          chRecTxPerc: values.chRecTxPerc,
          chRefundCap: values.chRefundCap,
          chAchCreditCap: values.chAchCreditCap,
        },

        cbInfo: {
          cbBusinessAddr: values.cbBusinessAddr,
          cbPEP: values.cbPEP,
          cbMainClients: values.cbMainClients,
          cbFromCountry: values.cbFromCountry,
          cbCurrency: values.cbCurrency,
          cbMonthlyVol: values.cbMonthlyVol,
          cbNumPayments: values.cbNumPayments,
          cbIncomingFunds: values.cbIncomingFunds,
          cbToCountry: values.cbToCountry,
          cbOutgoingFunds: values.cbOutgoingFunds,
        },

        incInfo: {
          incSuggestedName: values.incSuggestedName,
          incOfficeInUSA: values.incOfficeInUSA,
          incCurrentMerchantName: values.incCurrentMerchantName,
          incExistingAddress: values.incExistingAddress,
          incCompanyDescription: values.incCompanyDescription,
          incMonthlyVolume: values.incMonthlyVolume,
          incAvgTransactionValue: values.incAvgTransactionValue,
          incBillingDescriptor1: values.incBillingDescriptor1,
          incCityField: values.incCityField,
          incBOName1: values.incBOName1,
          incBONationality1: values.incBONationality1,
          incBOAddress1: values.incBOAddress1,
          incBOShares1: values.incBOShares1,
          incBOName2: values.incBOName2,
          incBONationality2: values.incBONationality2,
          incBOAddress2: values.incBOAddress2,
          incBOShares2: values.incBOShares2,
          incDirectorName1: values.incDirectorName1,
          incDirectorNationality1: values.incDirectorNationality1,
          incDirectorName2: values.incDirectorName2,
          incDirectorNationality2: values.incDirectorNationality2,
        },

        abInfo: {
          abProduct: values.abProduct,
          abProjectedVolume: values.abProjectedVolume,
          abProjectedTxCount: values.abProjectedTxCount,
          abLowestTxAmount: values.abLowestTxAmount,
          abHighestTxAmount: values.abHighestTxAmount,
          abAvgTxAmount: values.abAvgTxAmount,
          abTargetMarkets: values.abTargetMarkets,
          abSalesTurnover: values.abSalesTurnover,
        },
      };

      // console.log('Formik Errors:', formik.errors);

      try {
        const currentLanguage = i18n.language || 'en';

        if (isPublicPath) {
          const freshToken = await getCaptchaToken();
          await precheckService.submitPrecheckPublic(payload, currentLanguage, freshToken);
        } else {
          await precheckService.submitPrecheck(payload, currentLanguage);
        }

        resetForm();
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setSuccessMessage(t('common.formSubmittedSuccessfully'));
        // navigate('/precheck');
      } catch (error) {
        console.error('Error saving precheck ecom form:', error);

        const { messages, fieldErrors } = parseApiError(error, {
          generic: t('common.unexpectedError'),
          network: t('common.networkError'),
        });

        // Surface the rejected fields inline as well as in the summary
        Object.entries(fieldErrors).forEach(([field, message]) => setFieldError(field, message));
        setErrorMessage(t('common.submissionFailed'));
        setErrorDetails(messages);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } finally {
        submittingRef.current = false;
      }
    },
  });

  useScrollToError(formik.errors, formik.submitCount, formik.isSubmitting);

  const servicesByRegion: { [key: string]: string[] } = {
    us: ['creditCard', 'affiliate', 'clearingHouse', 'alternativeBilling', 'corporateBanking', 'whitelabelGateway'],
    eu: ['creditCard', 'affiliate', 'alternativeBilling', 'corporateBanking', 'whitelabelGateway'],
    other: ['creditCard', 'affiliate', 'whitelabelGateway'],
  };

  const handleRegionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedRegion = event.target.value;

    formik.setFieldValue('reg', selectedRegion);
    formik.setFieldValue(
      'srv',
      formik.values.srv.filter((service: string) => servicesByRegion[selectedRegion].includes(service))
    );
  };

  // Update validation schema when selected services change
  useEffect(() => {
    setValidationSchema(dynamicPrecheckFormSchema(formik.values.srv));
  }, [formik.values.srv]);

  const content = (
    <>
      <h2 className="text-4xl font-bold mb-8 text-slate-900">{t('prechecks.title')}</h2>

      <SuccessMessage message={successMessage} />

      <ErrorMessage message={errorMessage} details={errorDetails} />

      <form onSubmit={formik.handleSubmit} className="max-w-4xl space-y-8">
        {/* <DebugFormikErrors /> */}

        <SectionHeader
          title={t('prechecks.regionAndServices', 'Region and Services')}
          icon={
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
          iconBgColor="bg-indigo-100"
          iconTextColor="text-indigo-600"
          titleColor="text-indigo-600"
        >
          <FormSelect
            id="reg"
            name="reg"
            label={t('prechecks.region')}
            value={formik.values.reg}
            onChange={handleRegionChange}
            onBlur={formik.handleBlur}
            options={[
              { value: 'us', label: t('common.unitedStates', 'United States') },
              { value: 'eu', label: t('common.europe', 'Europe') },
              { value: 'other', label: t('common.other', 'Other') },
            ]}
            error={formik.errors.reg}
            touched={formik.touched.reg}
            required
          />

          <div className="bg-gradient-to-r from-slate-50 to-slate-100 border border-slate-200 rounded-lg p-6">
            <p className="text-base font-semibold text-slate-700 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
              {t('prechecks.servicesPrompt', 'Please select the services you are interested in')} <span className="text-red-500">*</span>
            </p>

            <div className="space-y-3 bg-white rounded-lg p-4 border border-slate-200">
              <Field className="flex items-center">
                <Input
                  type="checkbox"
                  name="srv"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  checked={formik.values.srv.includes('creditCard')}
                  value="creditCard"
                  disabled={!servicesByRegion[formik.values.reg].includes('creditCard')}
                  className="w-5 h-5 text-blue-600 bg-white border-slate-300 rounded focus:ring-2 focus:ring-blue-500 transition duration-150 ease-in-out cursor-pointer"
                />
                <Label className="text-base font-medium text-slate-700 ml-3 cursor-pointer">{t('prechecks.creditCardProcessing')}</Label>
              </Field>

              <Field className="flex items-center">
                <Input
                  type="checkbox"
                  name="srv"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  checked={formik.values.srv.includes('clearingHouse')}
                  value="clearingHouse"
                  disabled={!servicesByRegion[formik.values.reg].includes('clearingHouse')}
                  className="w-5 h-5 text-blue-600 bg-white border-slate-300 rounded focus:ring-2 focus:ring-blue-500 transition duration-150 ease-in-out cursor-pointer"
                />
                <Label className="text-base font-medium text-slate-700 ml-3 cursor-pointer">{t('prechecks.clearingHouse')}</Label>
              </Field>

              <Field className="flex items-center">
                <Input
                  type="checkbox"
                  name="srv"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  checked={formik.values.srv.includes('corporateBanking')}
                  value="corporateBanking"
                  disabled={!servicesByRegion[formik.values.reg].includes('corporateBanking')}
                  className="w-5 h-5 text-blue-600 bg-white border-slate-300 rounded focus:ring-2 focus:ring-blue-500 transition duration-150 ease-in-out cursor-pointer"
                />
                <Label className="text-base font-medium text-slate-700 ml-3 cursor-pointer">{t('prechecks.corporateBanking')}</Label>
              </Field>

              {/* <Field className="flex items-center">
                <Input
                  type="checkbox"
                  name="srv"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  checked={formik.values.srv.includes('incorporation')}
                  value="incorporation"
                  disabled={!servicesByRegion[formik.values.reg].includes('incorporation')}
                  className="w-5 h-5 text-blue-600 bg-white border-slate-300 rounded focus:ring-2 focus:ring-blue-500 transition duration-150 ease-in-out cursor-pointer"
                />
                <Label className="text-base font-medium text-slate-700 ml-3 cursor-pointer">US incorporation</Label>
              </Field> */}

              <Field className="flex items-center">
                <Input
                  type="checkbox"
                  name="srv"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  checked={formik.values.srv.includes('affiliate')}
                  value="affiliate"
                  disabled={!servicesByRegion[formik.values.reg].includes('affiliate')}
                  className="w-5 h-5 text-blue-600 bg-white border-slate-300 rounded focus:ring-2 focus:ring-blue-500 transition duration-150 ease-in-out cursor-pointer"
                />
                <Label className="text-base font-medium text-slate-700 ml-3 cursor-pointer">{t('prechecks.affiliatePayoutProgram')}</Label>
              </Field>

              <Field className="flex items-center">
                <Input
                  type="checkbox"
                  name="srv"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  checked={formik.values.srv.includes('whitelabelGateway')}
                  value="whitelabelGateway"
                  disabled={!servicesByRegion[formik.values.reg].includes('whitelabelGateway')}
                  className="w-5 h-5 text-blue-600 bg-white border-slate-300 rounded focus:ring-2 focus:ring-blue-500 transition duration-150 ease-in-out cursor-pointer"
                />
                <Label className="text-base font-medium text-slate-700 ml-3 cursor-pointer">{t('prechecks.whitelabelGateway')}</Label>
              </Field>

              <Field className="flex items-center">
                <Input
                  type="checkbox"
                  name="srv"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  checked={formik.values.srv.includes('alternativeBilling')}
                  value="alternativeBilling"
                  disabled={!servicesByRegion[formik.values.reg].includes('alternativeBilling')}
                  className="w-5 h-5 text-blue-600 bg-white border-slate-300 rounded focus:ring-2 focus:ring-blue-500 transition duration-150 ease-in-out cursor-pointer"
                />
                <Label className="text-base font-medium text-slate-700 ml-3 cursor-pointer">{t('prechecks.alternativeBilling')}</Label>
              </Field>

              {formik.touched.srv && formik.errors.srv ? (
                <div className="text-red-700 text-sm mt-2 bg-red-50 border border-red-200 p-2 rounded">{formik.errors.srv}</div>
              ) : null}
            </div>
          </div>
        </SectionHeader>

        <GeneralInformation formik={formik} />

        {formik.values.srv.includes('creditCard') && <CreditCardProcessing formik={formik} />}

        {formik.values.srv.includes('clearingHouse') && <ClearingHouse formik={formik} />}

        {formik.values.srv.includes('affiliate') && <AffiliatePayoutProgram formik={formik} />}

        {formik.values.srv.includes('whitelabelGateway') && <WhitelabelGateway formik={formik} />}

        {formik.values.srv.includes('corporateBanking') && <CorporateBanking formik={formik} />}

        {/* formik.values.srv.includes('incorporation') && <Incorporation formik={formik} /> */}

        {formik.values.srv.includes('alternativeBilling') && <AlternativeBilling formik={formik} />}

        <ErrorSummary errors={formik.errors} title={t('prechecksPOS.pleaseFixTheFollowingErrors')} />

        <SubmitButton
          label={t('common.submit')}
          isSubmitting={formik.isSubmitting}
          colorScheme="blue"
        />
      </form>
    </>
  );

  return isPublicPath ? (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 font-brand">
      <div className="flex flex-row flex-nowrap min-h-full justify-center relative">
        <main className="p-4 sm:p-6 md:p-8 w-full max-w-5xl mx-auto">
          <div className="flex justify-center mb-8">
            <BrandLogo brand={brand} className="h-16 w-auto" variant="dark" />
          </div>
          <div className="flex justify-center">
            <div className="w-full">{content}</div>
          </div>
        </main>

        {/* Language Switcher for public route */}
        <div className="absolute top-6 right-6">
          <LanguageSwitcher />
        </div>
      </div>
    </div>
  ) : (
    <div className="flex justify-center">
      <div className="w-full max-w-5xl">{content}</div>
    </div>
  );
};

export default PrecheckForm;
